import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase/supabase';
import { useAuth } from './AuthContext';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within a DataProvider');
  return context;
};

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentProject, setCurrentProject] = useState(null);
  const [inventoryData, setInventoryData] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (user) {
      fetchInitialData();
    } else {
      resetState();
    }
  }, [user]);

  const resetState = () => {
    setCurrentProject(null);
    setInventoryData([]);
    setIncidents([]);
    setLocations([]);
    setIsLoading(false);
  };

  // --- 1. TRANSFORMACIÓN CON REGLAS DE VISIBILIDAD ---
  const transformInventoryData = (employees, items, locations, userRole) => {
    const locMap = new Map(locations.map(l => [l.id, l]));
    const garmentsByEmployee = new Map();

    items.forEach(item => {
      // Regla de Visibilidad: Si está retirada, solo el Manager la ve
      const unlinkedStatus = item.metadata?.unlinked_status;
      if (unlinkedStatus === 'withdrawn' && userRole !== 'manager') {
        return; 
      }

      if (!garmentsByEmployee.has(item.employee_id)) {
        garmentsByEmployee.set(item.employee_id, []);
      }
      garmentsByEmployee.get(item.employee_id).push(item);
    });

    return employees.map(emp => {
      const location = locMap.get(emp.location_id);
      const empGarments = garmentsByEmployee.get(emp.id) || [];
      
      return {
        id: emp.id,
        owner: emp.full_name,
        employeeCode: emp.external_id,
        wardrobeName: location?.name || 'Sin asignar',
        wardrobeOrder: location?.sort_order || 9999,
        locker: emp.locker_number || 'N/A',
        metadata: emp.metadata || {},
        garments: empGarments.map(i => ({
          garment: i.item_name,
          size: i.size,
          barcode: i.barcode,
          rfid: i.metadata?.RFID || null,
          metadata: i.metadata || {}
        }))
      };
    }).filter(emp => emp.garments.length > 0 || !isLoading); 
  };

  const fetchAll = async (table, queryBuilder) => {
    let allData = [];
    let page = 0;
    const PAGE_SIZE = 1000;
    while (true) {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error } = await queryBuilder.range(from, to);
      if (error) throw error;
      if (!data || data.length === 0) break;
      allData = allData.concat(data);
      if (data.length < PAGE_SIZE) break;
      page++;
    }
    return { data: allData };
  };

  const fetchInitialData = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const { data: projectDetails, error: projectError } = await supabase
        .from('user_project_details')
        .select('*')
        .eq('user_id', user.id)
        .limit(1)
        .single();

      if (projectError) throw projectError;
      
      const newProject = {
        id: projectDetails.project_id,
        name: projectDetails.project_name,
        code: projectDetails.project_code,
        role: projectDetails.role,
        last_import_info: projectDetails.last_import_info || {}
      };
      setCurrentProject(newProject);

      const projectId = projectDetails.project_id;
      const [empRes, itemRes, locRes, incRes] = await Promise.all([
        fetchAll('employees', supabase.from('employees').select('*').eq('project_id', projectId).eq('active', true)),
        fetchAll('inventory_items', supabase.from('inventory_items').select('*').eq('project_id', projectId).eq('active', true)),
        fetchAll('locations', supabase.from('locations').select('*').eq('project_id', projectId).eq('active', true).order('sort_order', { ascending: true })),
        fetchAll('incidents', supabase.from('incidents').select('*').eq('project_id', projectId).order('created_at', { ascending: false }))
      ]);

      setLocations(locRes.data);
      const optimizedData = transformInventoryData(empRes.data, itemRes.data, locRes.data, newProject.role);
      setInventoryData(optimizedData);

      const empLookup = new Map(empRes.data.map(e => [e.id, e]));
      const locLookup = new Map(locRes.data.map(l => [l.id, l]));
      
      setIncidents(incRes.data.map(inc => {
        const emp = empLookup.get(inc.employee_id);
        const loc = emp ? locLookup.get(emp.location_id) : null;
        return {
          ...inc,
          wardrobeName: loc?.name || 'Sin asignar',
          locker: emp?.locker_number || 'N/A',
          owner: emp?.full_name || 'Desconocido',
          employeeCode: emp?.external_id || ''
        };
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- 2. LÓGICA DE ESTADOS Y FLUJO DE TRABAJO ---
  const manageUnlinkedGarment = async (barcode, action) => {
    if (!currentProject) return;
    setIsSyncing(true);
    try {
      const { data: currentItem } = await supabase
        .from('inventory_items')
        .select('metadata, active')
        .eq('project_id', currentProject.id)
        .eq('barcode', barcode)
        .single();

      if (!currentItem) throw new Error('Prenda no encontrada');

      let newMetadata = { ...currentItem.metadata };
      let newActiveState = currentItem.active;

      // PROCESAMIENTO SEGÚN ACCIÓN
      if (action === 'withdrawn') {
        // Estado: Retirada del vestuario (visible solo para Managers)
        newMetadata.unlinked_status = 'withdrawn';
        newMetadata.withdrawn_by = user.email;
        newMetadata.withdrawn_at = new Date().toISOString();
      } 
      else if (action === 'undo_withdrawn') {
        // Estado: Restaurar (vuelve a ser visible para todos)
        delete newMetadata.unlinked_status;
        delete newMetadata.withdrawn_by;
        delete newMetadata.withdrawn_at;
      }
      else if (action === 'sent') {
        // Estado: Enviada a fábrica/lavandería (desaparece de la base de datos operativa)
        newMetadata.unlinked_status = 'sent';
        newMetadata.sent_by = user.email;
        newMetadata.sent_at = new Date().toISOString();
        newActiveState = false; // Ocultación global definitiva
      }

      const { error } = await supabase
        .from('inventory_items')
        .update({ 
          metadata: newMetadata,
          active: newActiveState 
        })
        .eq('project_id', currentProject.id)
        .eq('barcode', barcode);

      if (error) throw error;

      // Refresco inmediato de la interfaz
      await fetchInitialData();
      return true;

    } catch (error) {
      console.error('Error managing unlinked garment:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  const saveInventoryData = async (jsonData, importMetadata = null) => {
    if (!currentProject || !jsonData.length) return;
    setIsSyncing(true);
    try {
      const projectId = currentProject.id;
      const excelHeaders = Object.keys(jsonData[0]);
      
      const findHeader = (partialText) => {
        return excelHeaders.find(h => h.toLowerCase().includes(partialText.toLowerCase()));
      };

      const KEY_WARDROBE = findHeader('WardrobeName');
      const KEY_LOCKER = findHeader('Locker');
      const KEY_OWNER = findHeader('Owner') || findHeader('Nombre') || findHeader('Empleado');
      const KEY_EMP_CODE = findHeader('EmployeeCode') || findHeader('Employee Code') || findHeader('EmployeeID') || findHeader('NoEmpleado');
      const KEY_BARCODE = findHeader('Barcode') || findHeader('Code') || findHeader('Bar Code') || findHeader('Codigo') || findHeader('RFID');
      const KEY_GARMENT = findHeader('Garment') || findHeader('Prenda') || findHeader('Article');
      const KEY_SIZE = findHeader('Size') || findHeader('Talla');

      if (!KEY_WARDROBE || !KEY_EMP_CODE || !KEY_BARCODE) {
        throw new Error('Faltan columnas críticas en el Excel.');
      }

      // Limpieza de datos antiguos
      await supabase.from('inventory_items').delete().eq('project_id', projectId);
      await supabase.from('employees').delete().eq('project_id', projectId);
      await supabase.from('locations').delete().eq('project_id', projectId);

      if (importMetadata) {
        await supabase.from('projects').update({ last_import_info: importMetadata }).eq('id', projectId);
      }

      // Carga de Vestuarios
      const rawLocNames = [...new Set(jsonData.map(row => String(row[KEY_WARDROBE] || '').trim()).filter(Boolean))];
      const getWeight = (name) => {
        const n = name.toUpperCase();
        if (n.includes('NAVE 9')) return 100;
        if (n.includes('NAVE 11')) return 101;
        const vMatch = n.match(/^V(\d+)$/);
        return vMatch ? parseInt(vMatch[1]) : 999;
      };
      rawLocNames.sort((a, b) => getWeight(a) - getWeight(b));

      const { data: locData } = await supabase.from('locations').upsert(
        rawLocNames.map((name, idx) => ({ project_id: projectId, name, sort_order: idx + 1, active: true })),
        { onConflict: 'project_id,name' }
      ).select();

      const locationMap = new Map(locData.map(l => [l.name, l.id]));

      // Carga de Empleados
      const empMap = new Map();
      jsonData.forEach(row => {
        const empCode = String(row[KEY_EMP_CODE] || '').trim();
        const locId = locationMap.get(String(row[KEY_WARDROBE] || '').trim());
        if (empCode && locId && !empMap.has(empCode)) {
          empMap.set(empCode, {
            project_id: projectId,
            external_id: empCode,
            full_name: String(row[KEY_OWNER] || 'Desconocido'),
            locker_number: String(row[KEY_LOCKER] || 'N/A'),
            location_id: locId,
            metadata: row,
            active: true
          });
        }
      });

      const { data: empData } = await supabase.from('employees').upsert(Array.from(empMap.values()), { onConflict: 'project_id,external_id' }).select();
      const empLookup = new Map(empData.map(e => [e.external_id, e.id]));

      // Carga de Items
      const itemsToInsert = jsonData.map((row, idx) => {
        const eCode = String(row[KEY_EMP_CODE] || '').trim();
        if (!empLookup.has(eCode)) return null;
        return {
          project_id: projectId,
          employee_id: empLookup.get(eCode),
          item_name: String(row[KEY_GARMENT] || 'Prenda'),
          size: String(row[KEY_SIZE] || 'U'),
          barcode: String(row[KEY_BARCODE] || `MISSING-${eCode}-${idx}`).trim(),
          metadata: row,
          active: true
        };
      }).filter(Boolean);

      const CHUNK_SIZE = 100;
      for (let i = 0; i < itemsToInsert.length; i += CHUNK_SIZE) {
        await supabase.from('inventory_items').upsert(itemsToInsert.slice(i, i + CHUNK_SIZE), { onConflict: 'project_id,barcode' });
      }

      await fetchInitialData();
      return true;
    } catch (error) {
      console.error('IMPORT ERROR:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  const searchInventory = (query, filters = {}) => {
    const hasQuery = query && query.trim();
    const hasFilters = Object.values(filters).some(v => v && v.trim());
    if (!hasQuery && !hasFilters) return [];

    return inventoryData.filter(item => {
      const mWardrobe = !filters.wardrobeName || item.wardrobeName === filters.wardrobeName;
      const mLocker = !filters.locker || item.locker === filters.locker;
      const mCode = !filters.employeeCode || item.employeeCode.toLowerCase().includes(filters.employeeCode.toLowerCase());
      const mQuery = !hasQuery || 
        item.owner.toLowerCase().includes(query.toLowerCase()) || 
        item.employeeCode.toLowerCase().includes(query.toLowerCase()) || 
        item.garments.some(g => g.barcode.toLowerCase().includes(query.toLowerCase()));

      return mQuery && mWardrobe && mLocker && mCode;
    });
  };

  const getUniqueWardrobes = () => locations.map(l => l.name);
  const getLockersByWardrobe = (w) => [...new Set(inventoryData.filter(i => i.wardrobeName === w).map(i => i.locker))].sort();

  const addIncident = async (incidentData) => {
    if (!user || !currentProject) return;
    try {
      const { data: emp } = await supabase.from('employees').select('id').eq('project_id', currentProject.id).eq('external_id', incidentData.employeeCode).single();
      const { error } = await supabase.from('incidents').insert([{
        project_id: currentProject.id,
        reported_by: user.id,
        employee_id: emp?.id,
        type: incidentData.type === 'cambio_talla' ? 'size_change' : (incidentData.type === 'daño' ? 'damage' : 'missing'),
        description: incidentData.description,
        status: 'pendiente',
        priority: incidentData.priority || 'medium'
      }]);
      if (error) throw error;
      await fetchInitialData();
    } catch (error) {
      console.error('Error adding incident:', error);
      throw error;
    }
  };

  return (
    <DataContext.Provider value={{
      inventoryData,
      incidents,
      locations,
      isLoading,
      isSyncing,
      currentProject,
      saveInventoryData,
      searchInventory,
      getUniqueWardrobes,
      getLockersByWardrobe,
      addIncident,
      manageUnlinkedGarment
    }}>
      {children}
    </DataContext.Provider>
  );
};